import { Routes, Route } from 'react-router-dom'
import { Layout } from './components/Layout/Layout'
import { HomePage } from './pages/HomePage'
import { TopicPage } from './pages/TopicPage'
import { EntryPage } from './pages/EntryPage'
import { CreateTopicPage } from './pages/CreateTopicPage'
import { ProfilePage } from './pages/ProfilePage'
import { LoginPage } from './pages/LoginPage'
import AboutPage from './pages/AboutPage'
import { RoomsPage } from './pages/RoomsPage'
import { RoomDetailPage } from './pages/RoomDetailPage'
import { AdminDashboard } from './pages/AdminDashboard'
import { NotFoundPage } from './pages/NotFoundPage'
import MessagesPage from './pages/MessagesPage'
import { SettingsPage } from './pages/SettingsPage'
import { FlowPage } from './pages/FlowPage'
import { HashtagPage } from './pages/HashtagPage'
import { ArticlePage } from './pages/ArticlePage'
import { BookmarksPage } from './pages/BookmarksPage'
import { SearchPage } from './pages/SearchPage'
import { ExternalProfilePage } from './pages/ExternalProfilePage'
import { ImagePage } from './pages/ImagePage'
import { ContactPage } from './pages/ContactPage'
import NotificationsPage from './pages/NotificationsPage'
import { useAuth } from './contexts/AuthContext'
import { Navigate } from 'react-router-dom'
import { ToastProvider } from './contexts/ToastContext'
import { LanguageProvider } from './contexts/LanguageContext'

function ProtectedRoute({ children, adminOnly = false }: { children: React.ReactNode, adminOnly?: boolean }) {
  const { pubkey, isAdmin, loading } = useAuth()

  if (loading) return null
  if (!pubkey) return <Navigate to="/login" />
  if (adminOnly && !isAdmin) return <Navigate to="/" />

  return <>{children}</>
}

function App() {
  return (
    <LanguageProvider>
      <ToastProvider>
        <Layout>
          <Routes>
            <Route path="/" element={<HomePage />} />
            {/* Specific routes must come before wildcard routes */}
            <Route path="/create-topic" element={<CreateTopicPage />} />
            <Route path="/entry/:entryId" element={<EntryPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/img/:noteId/:index" element={<ImagePage />} />
            <Route path="/profile/:username?" element={<ProfilePage />} />
            <Route path="/u/:username" element={<ExternalProfilePage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/rooms" element={<RoomsPage />} />
            <Route path="/rooms/:roomId" element={<RoomDetailPage />} />
            <Route path="/explore" element={<Navigate to="/flow/mavililer" replace />} />
            <Route path="/flow" element={<Navigate to="/flow/mavililer" replace />} />
            <Route path="/flow/:type" element={<FlowPage />} />
            <Route path="/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />
            <Route path="/hashtag/:hashtag" element={<HashtagPage />} />
            <Route path="/article/:identifier" element={<ArticlePage />} />
            <Route path="/bookmarks" element={<ProtectedRoute><BookmarksPage /></ProtectedRoute>} />
            <Route path="/search" element={<SearchPage />} />
            <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
            {/* Legacy URL format: /topic/:slug */}
            <Route path="/topic/:slug" element={<TopicPage />} />
            {/* New URL format: /:topicSlug (must be last to avoid catching other routes) */}
            <Route path="/admin" element={<ProtectedRoute adminOnly><AdminDashboard /></ProtectedRoute>} />
            <Route path="/inbox" element={<ProtectedRoute><MessagesPage /></ProtectedRoute>} />
            <Route path="/inbox/:pubkey" element={<ProtectedRoute><MessagesPage /></ProtectedRoute>} />
            {/* New URL format: /:topicSlug (at the bottom to avoid catching static routes) */}
            <Route path="/:topicSlug" element={<TopicPage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Layout>
      </ToastProvider>
    </LanguageProvider>
  )
}

export default App